// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import {
  FormControl,
  Button,
  Modal,
  Box,
  IconButton,
  Typography,
  Chip,
  Divider,
  Stack,
  Pagination,
  CircularProgress,
  TextField,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import FilterAltOffRoundedIcon from '@mui/icons-material/FilterAltOffRounded';
import { useGetDeviceBlockLog } from 'services/DeviceService';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 800,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
};

const API_PAGE_SIZE = 15;

const pill = {
  manual: {
    bgcolor: '#EDEBFB',
    color: '#5B4FE0',
  },
  rotina: {
    bgcolor: '#FDECD2',
    color: '#C77A16',
  },
  automatica: {
    bgcolor: '#E4F1FB',
    color: '#1E7EB8',
  },
};

const typeLabel = {
  manual: 'Manual',
  rotina: 'Rotina',
  automatica: 'Correção automática',
};

const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});
const timeFormatter = new Intl.DateTimeFormat('pt-BR', {
  hour: '2-digit',
  minute: '2-digit',
});

function mapEvent(event) {
  const occurredAt = new Date(event.occurredAt);
  const action = event.type === 'RELEASE' ? 'unlock' : 'lock';

  let type;
  let description;
  switch (event.origin.kind) {
    case 'MANUAL':
      type = 'manual';
      description = `Feito por: ${event.origin.userName}`;
      break;
    case 'ROUTINE':
      type = 'rotina';
      description = `Rotina: ${event.origin.routineName}`;
      break;
    case 'AUTO_CORRECTION':
    default:
      type = 'automatica';
      description = 'Correção automática do sistema';
      break;
  }

  return {
    id: occurredAt.getTime() + '-' + event.type + '-' + event.origin.kind,
    action,
    type,
    title: action === 'unlock' ? 'Desbloqueio' : 'Bloqueio',
    description,
    time: timeFormatter.format(occurredAt),
    dateKey: dateFormatter.format(occurredAt),
  };
}

function groupByDate(items) {
  const groups = [];
  const groupByKey = new Map();

  items.forEach((item) => {
    if (!groupByKey.has(item.dateKey)) {
      const group = { date: item.dateKey, data: [] };
      groupByKey.set(item.dateKey, group);
      groups.push(group);
    }
    groupByKey.get(item.dateKey).data.push(item);
  });

  return groups;
}

function LogIcon({ action }) {
  const isUnlock = action === 'unlock';
  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        bgcolor: isUnlock ? '#E3F5EA' : '#FCE7E5',
        color: isUnlock ? '#2E9A5B' : '#E0463F',
      }}
    >
      {isUnlock ? (
        <LockOpenRoundedIcon fontSize="small" />
      ) : (
        <LockRoundedIcon fontSize="small" />
      )}
    </Box>
  );
}

function LogRow({ data }) {
  const isUnlock = data.action === 'unlock';
  const pillStyle = pill[data.type];

  return (
    <Box sx={{ py: 2 }}>
      <Stack direction="row" alignItems="flex-start" spacing={2}>
        <LogIcon action={data.action} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '16px',
                color: isUnlock ? '#2E9A5B' : '#E0463F',
              }}
            >
              {data.title}
            </Typography>
            <Chip
              label={typeLabel[data.type]}
              size="small"
              sx={{
                ...pillStyle,
                fontWeight: 600,
                height: 24,
                '& .MuiChip-label': { px: 1 },
              }}
            />
          </Stack>
          <Typography sx={{ color: 'text.secondary', mt: 0.5 }}>
            {data.description}
          </Typography>
        </Box>

        <Typography sx={{ color: 'text.secondary', fontWeight: 500, whiteSpace: 'nowrap' }}>
          {data.time}
        </Typography>
      </Stack>
    </Box>
  );
}

function DateGroup({ group, isLast }) {
  return (
    <Box>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '0.8rem',
          letterSpacing: 0.5,
          color: 'text.secondary',
          textTransform: 'uppercase',
          mb: 0.5,
          mt: 2,
        }}
      >
        {group.date}
      </Typography>

      {group.data.map((data, idx) => (
        <React.Fragment key={data.id}>
          <LogRow data={data} />
          {idx < group.data.length - 1 && <Divider />}
        </React.Fragment>
      ))}

      {!isLast && <Divider sx={{ mt: 1, mb: 1 }} />}
    </Box>
  );
}


function LogsModal({ deviceName, idUser, idRouter, idDevice }) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);

  const [startDateInput, setStartDateInput] = useState('');
  const [endDateInput, setEndDateInput] = useState('');
  const [appliedFilter, setAppliedFilter] = useState({ startDate: '', endDate: '' });
  const [dateError, setDateError] = useState('');

  const { data, loading, error, request } = useGetDeviceBlockLog(
    idUser,
    idRouter,
    idDevice,
    page,
    appliedFilter.startDate || undefined,
    appliedFilter.endDate || undefined,
  );

  const handleOpen = () => {
    setPage(1);
    setStartDateInput('');
    setEndDateInput('');
    setAppliedFilter({ startDate: '', endDate: '' });
    setDateError('');
    setOpen(true);
  };
  const handleClose = () => setOpen(false);
  const handlePageChange = (_event, value) => setPage(value);

  const handleApplyFilter = () => {
    if (startDateInput && endDateInput && endDateInput < startDateInput) {
      setDateError('A data final deve ser maior ou igual à data inicial.');
      return;
    }
    setDateError('');
    setPage(1);
    setAppliedFilter({ startDate: startDateInput, endDate: endDateInput });
  };

  const handleClearFilter = () => {
    setStartDateInput('');
    setEndDateInput('');
    setDateError('');
    setPage(1);
    setAppliedFilter({ startDate: '', endDate: '' });
  };

  const hasActiveFilter = Boolean(appliedFilter.startDate || appliedFilter.endDate);

  useEffect(() => {
    if (!open) return;
    request();
  }, [open, page, idRouter, idDevice, appliedFilter.startDate, appliedFilter.endDate]);

  const events = useMemo(() => (data?.events ?? []).map(mapEvent), [data]);
  const groups = useMemo(() => groupByDate(events), [events]);
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / API_PAGE_SIZE));

  return (
    <>
      <FormControl>
        <Button
          variant="outlined"
          startIcon={<AccessTimeIcon fontSize="small" />}
          sx={{ fontWeight: 700 }}
          onClick={handleOpen}
        >
          Logs de Bloqueio/Desbloqueio
        </Button>
      </FormControl>

      <Modal open={open} onClose={handleClose}>
        <Box sx={{ ...style, display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography
                variant="h2"
                component="h2"
                sx={{ fontWeight: 800, color: '#2C2A7C' }}
              >
                Logs de bloqueio/desbloqueio
              </Typography>
              <Typography variant="body1" component="p" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {deviceName}
              </Typography>
            </Box>

            <IconButton onClick={handleClose} sx={{ bgcolor: '#F2F1FA' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Divider sx={{ mt: 2.5, mb: 2 }} />

          <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
            <TextField
              label="Data inicial"
              type="date"
              size="small"
              value={startDateInput}
              onChange={(e) => setStartDateInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
              error={Boolean(dateError)}
            />
            <TextField
              label="Data final"
              type="date"
              size="small"
              value={endDateInput}
              onChange={(e) => setEndDateInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
              error={Boolean(dateError)}
              helperText={dateError || ' '}
            />
            <Button variant="contained" sx={{ fontWeight: 700 }} onClick={handleApplyFilter}>
              Filtrar
            </Button>
            {hasActiveFilter && (
              <IconButton
                onClick={handleClearFilter}
              >
                <FilterAltOffRoundedIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>

          <Divider sx={{ mt: 1, mb: 1 }} />

          <Box sx={{ overflowY: 'auto', pr: 1, flex: 1, minHeight: 200 }}>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={28} />
              </Box>
            )}

            {!loading && error && (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <Typography sx={{ color: '#E0463F', fontWeight: 600 }}>
                  Não foi possível carregar o histórico. Tente novamente.
                </Typography>
              </Box>
            )}

            {!loading && !error && groups.length === 0 && (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <Typography sx={{ color: 'text.secondary' }}>
                  Nenhum evento de bloqueio/desbloqueio encontrado.
                </Typography>
              </Box>
            )}

            {!loading &&
              !error &&
              groups.map((group, idx) => (
                <DateGroup key={group.date} group={group} isLast={idx === groups.length - 1} />
              ))}
          </Box>

          {!loading && !error && pageCount > 1 && (
            <>
              <Divider sx={{ mt: 1, mb: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  shape="rounded"
                />
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </>
  );
}

export default LogsModal;